'use client';

import React, { useEffect, useRef, useState } from 'react';

interface SpotlightOverlayProps {
  /** Darkness outside the light cone. */
  color: string;
  /** 0-100: how dark the area outside the cone gets. */
  opacity: number;
  /** Radius of the light cone in px. */
  size: number;
  /** 0-100: height (in % of the section) where the light rests and around which it sweeps. */
  restY?: number;
  isAdmin?: boolean;
}

const REST_X = 62;

/**
 * Section "Reflektor" (stage spotlight): darkens the section's background except for a soft
 * circle of light. With a mouse the light follows the cursor (eased with rAF, only while it
 * moves); on touch screens it sweeps across on its own via a CSS animation of the registered
 * --spot-x/--spot-y properties (globals.css). Under prefers-reduced-motion and in the editor
 * canvas it stays still. Positioned against BlockRenderer's relative wrapper, like the overlay.
 */
export function SpotlightOverlay({ color, opacity, size, restY = 42, isAdmin }: SpotlightOverlayProps) {
  const REST_Y = restY;
  const ref = useRef<HTMLDivElement>(null);
  const [auto, setAuto] = useState(!isAdmin);

  useEffect(() => {
    const el = ref.current;
    const host = el?.parentElement;
    if (!el || !host || isAdmin) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setAuto(false);
      return;
    }
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;

    setAuto(false);
    let x = REST_X;
    let y = REST_Y;
    let tx = REST_X;
    let ty = REST_Y;
    let frame = 0;
    const paint = () => {
      el.style.setProperty('--spot-x', `${x}%`);
      el.style.setProperty('--spot-y', `${y}%`);
    };
    const tick = () => {
      x += (tx - x) * 0.14;
      y += (ty - y) * 0.14;
      paint();
      frame = Math.abs(tx - x) + Math.abs(ty - y) > 0.05 ? requestAnimationFrame(tick) : 0;
    };
    const start = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };
    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      if (!r.width || !r.height) return;
      tx = ((e.clientX - r.left) / r.width) * 100;
      ty = ((e.clientY - r.top) / r.height) * 100;
      start();
    };
    const onLeave = () => {
      tx = REST_X;
      ty = REST_Y;
      start();
    };
    paint();
    host.addEventListener('pointermove', onMove, { passive: true });
    host.addEventListener('pointerleave', onLeave, { passive: true });
    return () => {
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerleave', onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isAdmin, REST_Y]);

  const radius = Math.max(80, size || 420);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-auto={auto ? 'true' : 'false'}
      className="builder-spotlight absolute inset-0 pointer-events-none"
      style={{
        background: `radial-gradient(circle ${radius}px at var(--spot-x) var(--spot-y), transparent 0, transparent ${Math.round(radius * 0.32)}px, ${color} ${radius}px)`,
        opacity: Math.min(100, Math.max(0, opacity)) / 100,
        borderRadius: 'inherit',
        ['--spot-cy' as any]: `${REST_Y}%`,
        ...(auto ? {} : { ['--spot-x' as any]: `${REST_X}%`, ['--spot-y' as any]: `${REST_Y}%` }),
      }}
    />
  );
}
